import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {TaskFilter} from '../shared/models/task-filter.enum';
import {AppLanguage} from "./language.service";

// Keep these exports if you use them elsewhere but use primitive types
export let DEFAULT_RECOMMENDATION_FILTER_ONLY_GOOD: boolean = false;
export let DEFAULT_FILTER_SHOW_NEUTRAL: boolean = false;
export let DEFAULT_DAY_FILTER_TASKS: TaskFilter = TaskFilter.All;


@Injectable({
    providedIn: 'root'
})
export class StorageService {

    private readonly platformId = inject(PLATFORM_ID);

    // Storage keys
    private readonly keyRecommendationOnlyGood = 'recommendation_only_good';
    private readonly keyDayFilterCategories = 'day_filter_categories';
    private readonly keyDayFilterTasks = 'day_filter_tasks';
    private readonly keyDayFilterShowNeutral = 'day_filter_show_neutral';

    private readonly keyLanguage = 'language';

    // Defaults
    private readonly defaultCategories: string[] = [
        'body', 'garden', 'health', 'house', 'life'
    ];

    private get isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    // --- Generic helpers ------------------------------------------------------

    setShowOnlyGood(value: boolean): void {
        this.setItem<boolean>(this.keyRecommendationOnlyGood, value);
    }

    getShowOnlyGood(): boolean {
        const value = this.getItem<boolean | null>(
            this.keyRecommendationOnlyGood,
            null
        );

        if (value === null) {
            return DEFAULT_RECOMMENDATION_FILTER_ONLY_GOOD;
        }

        return value === true;
    }

    // --- API: Show Only Good --------------------------------------------------

    setDayFilterCategories(categories: string[]): void {
        this.setItem<string[]>(this.keyDayFilterCategories, categories);
    }

    getDayFilterCategories(): string[] {
        return this.getItem<string[]>(
            this.keyDayFilterCategories,
            this.defaultCategories
        );
    }

    // --- API: Day Filter Categories -------------------------------------------

    setDayFilterTasks(filter: TaskFilter): void {
        this.setItem<TaskFilter>(this.keyDayFilterTasks, filter);
    }

    getDayFilterTasks(): TaskFilter {
        return this.getItem<TaskFilter>(
            this.keyDayFilterTasks,
            DEFAULT_DAY_FILTER_TASKS
        );
    }

    // --- API: Day Filter Tasks ------------------------------------------------

    setDayFilterShowNeutral(value: boolean): void {
        this.setItem<boolean>(this.keyDayFilterShowNeutral, value);
    }

    getDayFilterShowNeutral(): boolean {
        const value = this.getItem<boolean | null>(
            this.keyDayFilterShowNeutral,
            null
        );

        if (value === null) {
            return DEFAULT_FILTER_SHOW_NEUTRAL;
        }

        return value === true;
    }

    // ✅ Language API
    setLanguage(lang: AppLanguage): void {
        this.setItem<AppLanguage>(this.keyLanguage, lang);
    }

    getLanguage(): AppLanguage | null {
        return this.getItem<AppLanguage | null>(this.keyLanguage, null);
    }

    // --- API: Day Filter Show Neutral ----------------------------------------

    private setItem<T>(key: string, value: T): void {
        if (!this.isBrowser) return;

        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // Fail silently: e.g. private mode / quota exceeded
        }
    }

    private getItem<T>(key: string, defaultValue: T): T {
        if (!this.isBrowser) {
            return defaultValue;
        }

        const stored = localStorage.getItem(key);
        if (stored === null) {
            return defaultValue;
        }

        try {
            return JSON.parse(stored) as T;
        } catch {
            return defaultValue;
        }
    }
}
